/**
 * How a surface tells a finishing walkthrough to close it.
 *
 * A tour can carry the user into a panel, so finishing has to carry them back out. Escape and the
 * surface's own ✕ cover most of that (`dismissRevealedAnchors`), but not a surface whose close is
 * guarded: the Recurring Shift panel raises "Discard Changes?" on ✕ even before anything is entered,
 * because opening from an empty `+` cell seeds a shift and the panel counts as dirty from that moment.
 *
 * A registered handler rather than a marked element in the DOM, deliberately. The DOM version worked,
 * but it meant a permanent *discard-my-unsaved-work* control sitting in every session of that panel for
 * every user — reachable by nothing today, and one stray selector away from destroying real edits with
 * no confirmation. Hiding it three ways (zero-size, `aria-hidden`, no tab stop) was the code saying it
 * should not have been in the DOM at all. Nothing is clickable now; the capability exists only for the
 * duration of the handler's own mount.
 *
 * This mirrors `registerCloseHandler` in `RecurringShiftMasterSchedule`, which already hands a close
 * upward the same way.
 */

let registered: (() => void) | null = null;

/**
 * Register (or, with `null`, clear) the current surface's discard-and-close.
 *
 * One at a time: the app shows one such panel at a time, and an overwrite means two surfaces both think
 * they own the close. That is a wiring mistake rather than a user-visible failure, so it warns and takes
 * the newer handler rather than throwing into a render.
 */
export const registerWalkthroughDismiss = (handler: (() => void) | null): void => {
	if (handler !== null && registered !== null) {
		console.warn("walkthrough: a second surface registered a dismiss handler; the newer one wins and the older close is now unreachable");
	}
	registered = handler;
};

/**
 * Clear `handler`, but only while it is still the registered one.
 *
 * React does not guarantee an unmounting component's cleanup runs before the next one's setup, so a
 * panel closing as another opens can otherwise erase the NEWER panel's registration on its way out — and
 * the next tour to finish would silently fall back to the generic Escape/✕ path instead of the guarded
 * close the live surface asked for.
 */
export const unregisterWalkthroughDismiss = (handler: () => void): void => {
	if (registered === handler) registered = null;
};

/** Run the registered close, if any. Returns whether it ran, so the caller can fall back to the DOM. */
export const dismissRegisteredSurface = (): boolean => {
	if (registered === null) return false;
	registered();
	return true;
};
